import MarkdownIt from "markdown-it";

/**
 * Last-resort guard against a leaked ask envelope reaching the reader.
 *
 * `parse_ask_output` on the backend already falls back to prose whenever it
 * cannot find an envelope, so most of the time this does nothing. It exists for
 * the one shape that falls through on purpose: an object carrying only an
 * `answer`. The backend's discriminator refuses to treat that as an envelope —
 * too weak a signal, since any JSON an answer happens to quote has an `answer`
 * key as readily as a real envelope does — and so returns it verbatim, which
 * would render as `{"answer": "…"}` in the box.
 *
 * **Only unwraps when the object is the entire reply.** That restriction is the
 * whole safety of this function. Scanning for an envelope *inside* prose would
 * mean an answer that quotes `{"answer": "your text here"}` — explaining this
 * very format, say — gets replaced wholesale by that fragment, turning a
 * complete answer into four words. Prose is the thing being protected here, so
 * a guard that can eat it is worse than no guard at all.
 *
 * The fence is handled because the prompt asks for "a single JSON code block",
 * so a leak arrives wrapped in one more often than not.
 *
 * Not `stripAgentEnvelope`: that keys on `message`, the thread agent's
 * envelope, and would never match this one.
 *
 * Never throws, and returns the content untouched for anything it is unsure of.
 */
const isAskEnvelope = (data: unknown): data is { answer: string } =>
    data !== null &&
    typeof data === "object" &&
    !Array.isArray(data) &&
    typeof (data as Record<string, unknown>).answer === "string";

/** Strip one wrapping ```…``` fence, if the whole string is inside it. */
const unfence = (text: string): string => {
    if (!text.startsWith("```")) return text;
    const lines = text.split("\n");
    if (lines.length < 2 || lines[lines.length - 1].trim() !== "```")
        return text;
    return lines.slice(1, -1).join("\n").trim();
};

export const stripAskEnvelope = (content: string): string => {
    if (!content) return content;

    const candidate = unfence(content.trim());
    if (!candidate.startsWith("{") || !candidate.endsWith("}")) return content;

    let parsed: unknown;
    try {
        parsed = JSON.parse(candidate);
    } catch {
        return content;
    }
    return isAskEnvelope(parsed) ? parsed.answer : content;
};

// html:false keeps raw HTML escaped, so rendering the model's prose is XSS-safe
// without a sanitizer — the same contract every other markdown site in this app
// relies on.
const md = new MarkdownIt({ html: false, linkify: true, breaks: true });

/**
 * One answer's prose as HTML, ready to hand to `v-html`.
 *
 * Stripped before rendering, not after: markdown-it would turn a leaked
 * envelope's braces and quotes into escaped HTML entities, and there would be
 * no JSON left to recognise.
 *
 * Here rather than in a component because there are now two places an answer is
 * drawn — the live one and every earlier one — and the escaping contract above
 * is the sort of thing that gets half-copied.
 */
export const renderAnswer = (content: string): string =>
    md.render(stripAskEnvelope(content || ""));
